import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Sun, Sunset, Moon, Sunrise } from "lucide-react";

const MESSAGES = {
    morning: [
        "Fresh start. Strong collections today.",
        "Every visit counts — make it a great morning.",
        "Trust is earned one customer at a time.",
    ],
    afternoon: [
        "Half-day done. Keep the energy up.",
        "Small steady wins build big numbers.",
        "One more call, one more visit.",
    ],
    evening: [
        "Final push — close out the day strong.",
        "Sun is setting, your impact isn't.",
        "Wrap with one last reminder out.",
    ],
    night: [
        "Working late — your dedication shows.",
        "Rest soon. Tomorrow we ride again.",
        "Logging final entries — thank you.",
    ],
};

function getSlot() {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return { key: "morning", label: "Good morning", icon: Sunrise, bg: "from-amber-50 to-orange-50", text: "text-amber-700" };
    if (h >= 12 && h < 17) return { key: "afternoon", label: "Good afternoon", icon: Sun, bg: "from-sky-50 to-blue-50", text: "text-sky-700" };
    if (h >= 17 && h < 21) return { key: "evening", label: "Good evening", icon: Sunset, bg: "from-rose-50 to-amber-50", text: "text-rose-700" };
    return { key: "night", label: "Good night", icon: Moon, bg: "from-indigo-50 to-slate-50", text: "text-indigo-700" };
}

const DISMISS_KEY = "wmf_welcome_dismissed";

export default function EmployeeWelcomePopup() {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [slot, setSlot] = useState(() => getSlot());

    useEffect(() => {
        if (!user || user.role !== "employee") return;
        const today = new Date().toISOString().slice(0, 10);
        const seen = localStorage.getItem(DISMISS_KEY);
        if (seen === `${user.id}:${today}`) return;
        setSlot(getSlot());
        setVisible(true);
    }, [user]);

    function dismiss() {
        if (user) {
            const today = new Date().toISOString().slice(0, 10);
            localStorage.setItem(DISMISS_KEY, `${user.id}:${today}`);
        }
        setVisible(false);
    }

    if (!visible || !user || user.role !== "employee") return null;
    const msgs = MESSAGES[slot.key];
    const msg = msgs[Math.floor(Math.random() * msgs.length)];
    const Icon = slot.icon;

    return (
        <div
            onClick={dismiss}
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm grid place-items-center px-5 animate-in fade-in duration-200"
            data-testid="employee-welcome-popup"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`relative w-full max-w-sm bg-gradient-to-br ${slot.bg} rounded-3xl p-6 sm:p-8 text-center shadow-2xl border border-white/40 animate-in zoom-in-95 duration-300`}
            >
                <div className={`mx-auto h-14 w-14 rounded-2xl bg-white grid place-items-center shadow-sm ${slot.text}`}>
                    <Icon className="h-7 w-7" strokeWidth={2} />
                </div>
                <div className={`mt-4 text-[11px] uppercase tracking-[0.2em] font-semibold ${slot.text}`}>
                    {slot.label}
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
                    {user.name?.split(" ")[0] || "There"}
                </h2>
                <p className="text-sm text-slate-700 mt-4 leading-relaxed">
                    {msg}
                </p>
                <button
                    onClick={dismiss}
                    data-testid="welcome-dismiss-btn"
                    className="mt-6 w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold tap"
                >
                    Let's go
                </button>
                <p className="text-[10px] text-slate-500 mt-3">tap anywhere to dismiss</p>
            </div>
        </div>
    );
}
